import { Button, styled, Tooltip } from "@mui/material";
import numeral from "numeral";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CustomDataGrid from "../../../common/js/components/CustomDataGrid";
import { pageSize } from "../utils/constants";
import { buildAddress, formatDate } from "../utils/operations";

const StyledLink = styled(Link)({
    textDecoration: "none",
    lineHeight: "1.2",
});

const StyledDealName = styled("span")({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
    wordWrap: "break-word",
    whiteSpace: "normal",
    lineHeight: "1.3",
});

const StyledDealCell = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const StyledAddress = styled("div")({
    fontSize: "12px",
});

const StyledButton = styled(Button)({
    height: "28px",
    marginLeft: "0px",
});

const DataAdminDealsGrid = ({
    dataAdminDeals,
    dealsPage,
    getDataAdminDeals,
    history,
    isLoading,
    resetDealsTab,
    setDealsPage,
    setDealsSort
}) => {
    const [sortModel, setSortModel] = useState([]);
    const dataDeals = dataAdminDeals?.content ?? [];
    const totalCount = dataAdminDeals?.totalElements ?? 0;

    useEffect(() => {
        resetDealsTab();
        getDataAdminDeals();
    }, []);

    const renderBrokers = (brokers) => {
        if (!brokers) return "";
        const brokerNames = brokers.split(",");
        if (brokerNames.length > 1) {
            return (
                <Tooltip title={brokers} placement="top">
                    <span>{`${brokerNames.length} Brokers`}</span>
                </Tooltip>
            );
        }
        return brokers;
    };

    const columns = [
        {
            field: "dealName",
            headerName: "Deal",
            flex: 2,
            minWidth: 250,
            renderCell: (params) => (
                <StyledDealCell>
                    <StyledLink to={`/deal/${params.id}`}>
                        <StyledDealName>
                            {`Deal No. ${params.id}${params.row?.dealName ? ` | ${params.row.dealName}` : ""}`}
                        </StyledDealName>
                    </StyledLink>
                </StyledDealCell>
            ),
        },
        {
            field: "address",
            headerName: "Address",
            flex: 2,
            minWidth: 280,
            sortable: false,
            renderCell: (params) => (
                <StyledDealCell>
                    <StyledAddress>{buildAddress(params.row?.primaryAddress ?? {})}</StyledAddress>
                </StyledDealCell>
            ),
        },
        {
            field: "brokers",
            headerName: "Brokers",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => renderBrokers(params.row?.brokers),
        },
        {
            field: "paymentAttachedDate",
            headerName: "Payment Attached Date",
            flex: 1,
            minWidth: 120,
            renderCell: (params) =>
                params.row.paymentAttachedDate
                    ? formatDate("MM-DD-YYYY")(params.row?.paymentAttachedDate)
                    : "Not Submitted",
        },
        {
            field: "propertyType",
            headerName: "Type",
            flex: 1,
            minWidth: 120,
            sortable: false,
            renderCell: (params) => `${params.row?.propertyType} ${params.row?.transactionType}`,
        },
        {
            field: "totalCommission",
            headerName: "Total Commission",
            hide: true,
            flex: 1,
            minWidth: 150,
            renderCell: (params) =>
                params.row?.totalCommission ? "$ " + numeral(params.row.totalCommission).format("0,0.00") : "",
        },
        {
            field: "totalKmCommission",
            headerName: "Total KM Commission",
            hide: true,
            flex: 1,
            minWidth: 150,
            renderCell: (params) =>
                params.row?.totalKmCommission ? "$ " + numeral(params.row.totalKmCommission).format("0,0.00") : "",
        },
        {
            field: "action",
            headerName: "Action",
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => {
                return (
                    <StyledButton variant="outlined" color="primary" onClick={() => history.push(`/deal/${params.id}`)}>
                        View
                    </StyledButton>
                );
            },
        },
    ];

    const handlePageChange = (value) => {
        setDealsPage(value);
        getDataAdminDeals();
    };

    const handleSortModelChange = (newSortModel) => {
        setDealsPage(1);
        setSortModel(newSortModel);
        const newSort =
            newSortModel[0]?.field && newSortModel[0]?.sort ? `${newSortModel[0].field},${newSortModel[0].sort}` : null;
        setDealsSort(newSort);
        getDataAdminDeals();
    };

    return (
        <CustomDataGrid
            rows={dataDeals}
            columns={columns}
            totalCount={totalCount}
            page={dealsPage}
            rowHeight={35}
            pageSize={pageSize}
            sortModel={sortModel}
            loading={isLoading}
            onSortModelChange={handleSortModelChange}
            onPageChange={(_, value) => handlePageChange(value)}
        />
    );
};

export default DataAdminDealsGrid;
